import { HTTP_STATUS } from '@ibe/constants';
/**
 * The canonical error-code registry.
 *
 * MASTER_DEVELOPMENT_PROMPT.md: "The registry is the single source of truth;
 * a code that is not in the registry must never appear in a response." Later
 * tasks extend this file rather than declaring private enums inside a service.
 *
 * Every code carries an HTTP status so that mapping lives in one place instead
 * of being re-decided at each route.
 */
export const ERROR_CODES = {
    // --- Generic (packages/errors, Task 03) ---
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    CONFLICT: 'CONFLICT',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    CONFIGURATION_ERROR: 'CONFIGURATION_ERROR',
    DATABASE_ERROR: 'DATABASE_ERROR',
    NETWORK_ERROR: 'NETWORK_ERROR',
    TIMEOUT: 'TIMEOUT',
    NOT_IMPLEMENTED: 'NOT_IMPLEMENTED',
    // --- Identity and access (Task 04) ---
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
    PASSWORD_EXPIRED: 'PASSWORD_EXPIRED',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    TOKEN_INVALID: 'TOKEN_INVALID',
    SESSION_EXPIRED: 'SESSION_EXPIRED',
    ACCESS_DENIED: 'ACCESS_DENIED',
    INSUFFICIENT_PERMISSION: 'INSUFFICIENT_PERMISSION',
    USER_NOT_FOUND: 'USER_NOT_FOUND',
    ROLE_NOT_FOUND: 'ROLE_NOT_FOUND',
    DUPLICATE_EMAIL: 'DUPLICATE_EMAIL',
    WEAK_PASSWORD: 'WEAK_PASSWORD',
    MFA_REQUIRED: 'MFA_REQUIRED',
    MFA_INVALID: 'MFA_INVALID',
    OTP_INVALID: 'OTP_INVALID',
    OTP_EXPIRED: 'OTP_EXPIRED',
    GUEST_ACCOUNT_NOT_FOUND: 'GUEST_ACCOUNT_NOT_FOUND',
    GUEST_EMAIL_NOT_VERIFIED: 'GUEST_EMAIL_NOT_VERIFIED',
    GUEST_ACCOUNT_EXISTS: 'GUEST_ACCOUNT_EXISTS',
    INVALID_TOKEN_AUDIENCE: 'INVALID_TOKEN_AUDIENCE',
    BOOKING_LOOKUP_FAILED: 'BOOKING_LOOKUP_FAILED',
    RESERVATION_ACCESS_DENIED: 'RESERVATION_ACCESS_DENIED',
    // --- Gateway (Task 05) ---
    GATEWAY_TIMEOUT: 'GATEWAY_TIMEOUT',
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
    RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
    IDEMPOTENCY_CONFLICT: 'IDEMPOTENCY_CONFLICT',
    INVALID_REQUEST: 'INVALID_REQUEST',
    CSRF_VALIDATION_FAILED: 'CSRF_VALIDATION_FAILED',
    PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
    // --- Business rules (Task 06) ---
    RULE_NOT_FOUND: 'RULE_NOT_FOUND',
    RULE_INVALID: 'RULE_INVALID',
    RULE_CONFLICT: 'RULE_CONFLICT',
    RULE_EVALUATION_FAILED: 'RULE_EVALUATION_FAILED',
    RULE_CONDITION_INVALID: 'RULE_CONDITION_INVALID',
    RULE_ACTION_INVALID: 'RULE_ACTION_INVALID',
    RULE_CIRCULAR_DEPENDENCY: 'RULE_CIRCULAR_DEPENDENCY',
    // --- Guest (Task 09) ---
    GUEST_NOT_FOUND: 'GUEST_NOT_FOUND',
    DUPLICATE_PHONE: 'DUPLICATE_PHONE',
    INVALID_EMAIL: 'INVALID_EMAIL',
    INVALID_PHONE: 'INVALID_PHONE',
    CONSENT_REQUIRED: 'CONSENT_REQUIRED',
    DOCUMENT_EXPIRED: 'DOCUMENT_EXPIRED',
    GUEST_BLOCKED: 'GUEST_BLOCKED',
    RIGHTS_REQUEST_CONFLICT: 'RIGHTS_REQUEST_CONFLICT',
    // --- Availability (Task 10) ---
    ROOM_NOT_AVAILABLE: 'ROOM_NOT_AVAILABLE',
    INVENTORY_EXCEEDED: 'INVENTORY_EXCEEDED',
    RESTRICTION_VIOLATION: 'RESTRICTION_VIOLATION',
    STOP_SELL_ACTIVE: 'STOP_SELL_ACTIVE',
    CTA_RESTRICTION: 'CTA_RESTRICTION',
    CTD_RESTRICTION: 'CTD_RESTRICTION',
    MIN_STAY_VIOLATION: 'MIN_STAY_VIOLATION',
    MAX_STAY_VIOLATION: 'MAX_STAY_VIOLATION',
    RELEASE_PERIOD_VIOLATION: 'RELEASE_PERIOD_VIOLATION',
    BLACKOUT_DATE: 'BLACKOUT_DATE',
    // --- Pricing (Task 11) ---
    INVALID_RATE: 'INVALID_RATE',
    RATE_NOT_FOUND: 'RATE_NOT_FOUND',
    PROMOTION_EXPIRED: 'PROMOTION_EXPIRED',
    PROMOTION_NOT_APPLICABLE: 'PROMOTION_NOT_APPLICABLE',
    INVALID_CURRENCY: 'INVALID_CURRENCY',
    EXCHANGE_RATE_UNAVAILABLE: 'EXCHANGE_RATE_UNAVAILABLE',
    POLICY_NOT_EDITABLE: 'POLICY_NOT_EDITABLE',
    POLICY_CANNOT_ACTIVATE: 'POLICY_CANNOT_ACTIVATE',
    // --- Reservation (Task 12) ---
    BOOKING_CONFLICT: 'BOOKING_CONFLICT',
    INVALID_DATES: 'INVALID_DATES',
    INVALID_OCCUPANCY: 'INVALID_OCCUPANCY',
    GUEST_REQUIRED: 'GUEST_REQUIRED',
    PROPERTY_INACTIVE: 'PROPERTY_INACTIVE',
    RATE_PLAN_NOT_APPLICABLE: 'RATE_PLAN_NOT_APPLICABLE',
    RESERVATION_NOT_FOUND: 'RESERVATION_NOT_FOUND',
    RESERVATION_ALREADY_CANCELLED: 'RESERVATION_ALREADY_CANCELLED',
    RESERVATION_CANNOT_MODIFY: 'RESERVATION_CANNOT_MODIFY',
    RESERVATION_CANNOT_CANCEL: 'RESERVATION_CANNOT_CANCEL',
    CANCELLATION_PENALTY_APPLIED: 'CANCELLATION_PENALTY_APPLIED',
    BOOKING_REFERENCE_COLLISION: 'BOOKING_REFERENCE_COLLISION',
    RESERVATION_CONFLICT: 'RESERVATION_CONFLICT',
    // --- PriceQuote validation/conversion (reservation flow) ---
    QUOTE_NOT_FOUND: 'QUOTE_NOT_FOUND',
    QUOTE_ALREADY_CONVERTED: 'QUOTE_ALREADY_CONVERTED',
    QUOTE_EXPIRED: 'QUOTE_EXPIRED',
    QUOTE_PROPERTY_MISMATCH: 'QUOTE_PROPERTY_MISMATCH',
    QUOTE_ROOM_MISMATCH: 'QUOTE_ROOM_MISMATCH',
    QUOTE_RATE_MISMATCH: 'QUOTE_RATE_MISMATCH',
    QUOTE_DATE_MISMATCH: 'QUOTE_DATE_MISMATCH',
    QUOTE_OCCUPANCY_MISMATCH: 'QUOTE_OCCUPANCY_MISMATCH',
    QUOTE_CURRENCY_MISMATCH: 'QUOTE_CURRENCY_MISMATCH',
    /** The quote was priced for an eligibility context the caller does not satisfy. */
    QUOTE_CONTEXT_MISMATCH: 'QUOTE_CONTEXT_MISMATCH',
    // --- PMS (Tasks 13, 14) ---
    PMS_TIMEOUT: 'PMS_TIMEOUT',
    PMS_AUTH_FAILED: 'PMS_AUTH_FAILED',
    PMS_UNAVAILABLE: 'PMS_UNAVAILABLE',
    PMS_SYNC_ERROR: 'PMS_SYNC_ERROR',
    // --- PMS webhook ingestion (Task 33) ---
    PMS_WEBHOOK_SIGNATURE_INVALID: 'PMS_WEBHOOK_SIGNATURE_INVALID',
    PMS_WEBHOOK_PAYLOAD_INVALID: 'PMS_WEBHOOK_PAYLOAD_INVALID',
    PMS_MAPPING_NOT_FOUND: 'PMS_MAPPING_NOT_FOUND',
    PMS_CHAIN_NOT_RESOLVED: 'PMS_CHAIN_NOT_RESOLVED',
    PMS_TAX_TREATMENT_UNCONFIGURED: 'PMS_TAX_TREATMENT_UNCONFIGURED',
    // --- Integration (Task 15) ---
    INTEGRATION_TIMEOUT: 'INTEGRATION_TIMEOUT',
    INTEGRATION_FAILED: 'INTEGRATION_FAILED',
    INTEGRATION_UNAVAILABLE: 'INTEGRATION_UNAVAILABLE',
    RETRY_EXHAUSTED: 'RETRY_EXHAUSTED',
    DEAD_LETTER_REACHED: 'DEAD_LETTER_REACHED',
    SYNC_CONFLICT: 'SYNC_CONFLICT',
    SYNC_FAILED: 'SYNC_FAILED',
    EVENT_PROCESSING_FAILED: 'EVENT_PROCESSING_FAILED',
    // --- Payment (Tasks 16-19) ---
    PAYMENT_FAILED: 'PAYMENT_FAILED',
    PAYMENT_DECLINED: 'PAYMENT_DECLINED',
    PAYMENT_TIMEOUT: 'PAYMENT_TIMEOUT',
    INVALID_PAYMENT_TOKEN: 'INVALID_PAYMENT_TOKEN',
    FRAUD_DETECTED: 'FRAUD_DETECTED',
    PAYMENT_GATEWAY_UNAVAILABLE: 'PAYMENT_GATEWAY_UNAVAILABLE',
    PAYMENT_AUTH_FAILED: 'PAYMENT_AUTH_FAILED',
    PAYMENT_NOT_FOUND: 'PAYMENT_NOT_FOUND',
    CAPTURE_EXCEEDS_AUTHORIZATION: 'CAPTURE_EXCEEDS_AUTHORIZATION',
    REFUND_EXCEEDS_CAPTURE: 'REFUND_EXCEEDS_CAPTURE',
    PAYMENT_ALREADY_CAPTURED: 'PAYMENT_ALREADY_CAPTURED',
    PAYMENT_ALREADY_VOIDED: 'PAYMENT_ALREADY_VOIDED',
    PAYMENT_CURRENCY_MISMATCH: 'PAYMENT_CURRENCY_MISMATCH',
    PAYMENT_STATE_INVALID: 'PAYMENT_STATE_INVALID',
    /** The reservation or rate plan has no linked payment policy to charge against. */
    PAYMENT_POLICY_INCOMPLETE: 'PAYMENT_POLICY_INCOMPLETE',
    RECONCILIATION_DISCREPANCY: 'RECONCILIATION_DISCREPANCY',
    /** Raised when a request body contains anything shaped like a card number. */
    PAYMENT_FIELD_NOT_ALLOWED: 'PAYMENT_FIELD_NOT_ALLOWED',
    // --- Notification (Task 20) ---
    NOTIFICATION_FAILED: 'NOTIFICATION_FAILED',
    TEMPLATE_NOT_FOUND: 'TEMPLATE_NOT_FOUND',
    PROVIDER_ERROR: 'PROVIDER_ERROR',
    INVALID_RECIPIENT: 'INVALID_RECIPIENT',
    TEMPLATE_RENDER_ERROR: 'TEMPLATE_RENDER_ERROR',
    QUEUE_FULL: 'QUEUE_FULL',
    DELIVERY_FAILED: 'DELIVERY_FAILED',
    /**
     * No usable gateway is configured for the channel/scope: no matching
     * tenant or property gateway row and no platform credentials. Retried by
     * the caller once configuration exists — the recipient was never contacted.
     */
    GATEWAY_NOT_CONFIGURED: 'GATEWAY_NOT_CONFIGURED',
    // --- Attachments ---
    ATTACHMENT_NOT_FOUND: 'ATTACHMENT_NOT_FOUND',
    /**
     * No tenant credentials and no global fallback. A 400 rather than a 500:
     * a tenant that has not supplied storage credentials is a setup problem the
     * caller can act on, not a fault in this service.
     */
    ATTACHMENT_STORAGE_NOT_CONFIGURED: 'ATTACHMENT_STORAGE_NOT_CONFIGURED',
    /**
     * Credentials resolved but S3 rejected them — wrong bucket, wrong region or
     * bad keys. A 400 like its sibling: whoever configured the tenant's storage
     * can fix it, and the portal treats 4xx as an operator-facing answer rather
     * than a generic server failure.
     */
    ATTACHMENT_STORAGE_MISCONFIGURED: 'ATTACHMENT_STORAGE_MISCONFIGURED',
    ATTACHMENT_TYPE_NOT_ALLOWED: 'ATTACHMENT_TYPE_NOT_ALLOWED',
    ATTACHMENT_TOO_LARGE: 'ATTACHMENT_TOO_LARGE',
    ATTACHMENT_KEY_OUT_OF_SCOPE: 'ATTACHMENT_KEY_OUT_OF_SCOPE',
    ATTACHMENT_OCR_FAILED: 'ATTACHMENT_OCR_FAILED',
    // --- Tenancy (Task 28) ---
    /** The Host header resolves to no tenant, or the tenant does not exist. */
    TENANT_NOT_FOUND: 'TENANT_NOT_FOUND',
    /** The token's tenant and the request's host disagree. */
    TENANT_MISMATCH: 'TENANT_MISMATCH',
    /** The tenant exists but is not ACTIVE. */
    TENANT_SUSPENDED: 'TENANT_SUSPENDED',
    /** The impersonation grant has expired or been revoked. */
    IMPERSONATION_EXPIRED: 'IMPERSONATION_EXPIRED',
    /**
     * A query ran with no tenant scope open.
     *
     * Internal by design. RLS would have returned zero rows regardless; this
     * code exists so the cause surfaces as an error at the call site rather
     * than as a mysteriously empty result set.
     */
    TENANT_CONTEXT_MISSING: 'TENANT_CONTEXT_MISSING',
    // --- Reference data and bulk import ---
    REFERENCE_IN_USE: 'REFERENCE_IN_USE',
    REFERENCE_RESTORE_CONFLICT: 'REFERENCE_RESTORE_CONFLICT',
    IMPORT_FILE_TOO_LARGE: 'IMPORT_FILE_TOO_LARGE',
    IMPORT_FILE_UNREADABLE: 'IMPORT_FILE_UNREADABLE',
    IMPORT_ROWS_INVALID: 'IMPORT_ROWS_INVALID',
    IMPORT_PLAN_NOT_FOUND: 'IMPORT_PLAN_NOT_FOUND',
    IMPORT_PLAN_STALE: 'IMPORT_PLAN_STALE',
};
const { BAD_REQUEST, UNAUTHORIZED: HTTP_UNAUTHORIZED, FORBIDDEN: HTTP_FORBIDDEN, NOT_FOUND: HTTP_NOT_FOUND, CONFLICT: HTTP_CONFLICT, UNPROCESSABLE_ENTITY, PAYLOAD_TOO_LARGE: HTTP_PAYLOAD_TOO_LARGE, TOO_MANY_REQUESTS, LOCKED, PAYMENT_REQUIRED, INTERNAL_SERVER_ERROR, BAD_GATEWAY, SERVICE_UNAVAILABLE: HTTP_SERVICE_UNAVAILABLE, GATEWAY_TIMEOUT: HTTP_GATEWAY_TIMEOUT, NOT_IMPLEMENTED: HTTP_NOT_IMPLEMENTED, } = HTTP_STATUS;
/**
 * Code-to-HTTP-status mapping.
 *
 * Any code absent from this map is a programming error, surfaced by the
 * registry-completeness test rather than by a 500 in production.
 */
export const ERROR_HTTP_STATUS = {
    // Generic
    VALIDATION_ERROR: UNPROCESSABLE_ENTITY,
    UNAUTHORIZED: HTTP_UNAUTHORIZED,
    FORBIDDEN: HTTP_FORBIDDEN,
    NOT_FOUND: HTTP_NOT_FOUND,
    CONFLICT: HTTP_CONFLICT,
    INTERNAL_ERROR: INTERNAL_SERVER_ERROR,
    CONFIGURATION_ERROR: INTERNAL_SERVER_ERROR,
    DATABASE_ERROR: INTERNAL_SERVER_ERROR,
    NETWORK_ERROR: BAD_GATEWAY,
    TIMEOUT: HTTP_GATEWAY_TIMEOUT,
    NOT_IMPLEMENTED: HTTP_NOT_IMPLEMENTED,
    // Identity
    INVALID_CREDENTIALS: HTTP_UNAUTHORIZED,
    ACCOUNT_LOCKED: LOCKED,
    PASSWORD_EXPIRED: HTTP_FORBIDDEN,
    TOKEN_EXPIRED: HTTP_UNAUTHORIZED,
    TOKEN_INVALID: HTTP_UNAUTHORIZED,
    SESSION_EXPIRED: HTTP_UNAUTHORIZED,
    ACCESS_DENIED: HTTP_FORBIDDEN,
    INSUFFICIENT_PERMISSION: HTTP_FORBIDDEN,
    USER_NOT_FOUND: HTTP_NOT_FOUND,
    ROLE_NOT_FOUND: HTTP_NOT_FOUND,
    DUPLICATE_EMAIL: HTTP_CONFLICT,
    WEAK_PASSWORD: UNPROCESSABLE_ENTITY,
    MFA_REQUIRED: HTTP_UNAUTHORIZED,
    MFA_INVALID: HTTP_UNAUTHORIZED,
    OTP_INVALID: HTTP_UNAUTHORIZED,
    OTP_EXPIRED: HTTP_UNAUTHORIZED,
    GUEST_ACCOUNT_NOT_FOUND: HTTP_NOT_FOUND,
    GUEST_EMAIL_NOT_VERIFIED: HTTP_FORBIDDEN,
    GUEST_ACCOUNT_EXISTS: HTTP_CONFLICT,
    INVALID_TOKEN_AUDIENCE: HTTP_FORBIDDEN,
    // Deliberately 401, never 404: a 404 would confirm the reference exists.
    BOOKING_LOOKUP_FAILED: HTTP_UNAUTHORIZED,
    RESERVATION_ACCESS_DENIED: HTTP_FORBIDDEN,
    // Gateway
    GATEWAY_TIMEOUT: HTTP_GATEWAY_TIMEOUT,
    SERVICE_UNAVAILABLE: HTTP_SERVICE_UNAVAILABLE,
    RATE_LIMIT_EXCEEDED: TOO_MANY_REQUESTS,
    IDEMPOTENCY_CONFLICT: HTTP_CONFLICT,
    INVALID_REQUEST: BAD_REQUEST,
    CSRF_VALIDATION_FAILED: HTTP_FORBIDDEN,
    PAYLOAD_TOO_LARGE: HTTP_PAYLOAD_TOO_LARGE,
    // Business rules
    RULE_NOT_FOUND: HTTP_NOT_FOUND,
    RULE_INVALID: UNPROCESSABLE_ENTITY,
    RULE_CONFLICT: HTTP_CONFLICT,
    RULE_EVALUATION_FAILED: INTERNAL_SERVER_ERROR,
    RULE_CONDITION_INVALID: UNPROCESSABLE_ENTITY,
    RULE_ACTION_INVALID: UNPROCESSABLE_ENTITY,
    RULE_CIRCULAR_DEPENDENCY: UNPROCESSABLE_ENTITY,
    // Guest
    GUEST_NOT_FOUND: HTTP_NOT_FOUND,
    DUPLICATE_PHONE: HTTP_CONFLICT,
    INVALID_EMAIL: UNPROCESSABLE_ENTITY,
    INVALID_PHONE: UNPROCESSABLE_ENTITY,
    CONSENT_REQUIRED: HTTP_FORBIDDEN,
    DOCUMENT_EXPIRED: UNPROCESSABLE_ENTITY,
    GUEST_BLOCKED: HTTP_FORBIDDEN,
    RIGHTS_REQUEST_CONFLICT: HTTP_CONFLICT,
    // Availability
    ROOM_NOT_AVAILABLE: HTTP_CONFLICT,
    INVENTORY_EXCEEDED: HTTP_CONFLICT,
    RESTRICTION_VIOLATION: UNPROCESSABLE_ENTITY,
    STOP_SELL_ACTIVE: HTTP_CONFLICT,
    CTA_RESTRICTION: UNPROCESSABLE_ENTITY,
    CTD_RESTRICTION: UNPROCESSABLE_ENTITY,
    MIN_STAY_VIOLATION: UNPROCESSABLE_ENTITY,
    MAX_STAY_VIOLATION: UNPROCESSABLE_ENTITY,
    RELEASE_PERIOD_VIOLATION: UNPROCESSABLE_ENTITY,
    BLACKOUT_DATE: UNPROCESSABLE_ENTITY,
    // Pricing
    INVALID_RATE: UNPROCESSABLE_ENTITY,
    RATE_NOT_FOUND: HTTP_NOT_FOUND,
    PROMOTION_EXPIRED: UNPROCESSABLE_ENTITY,
    PROMOTION_NOT_APPLICABLE: UNPROCESSABLE_ENTITY,
    INVALID_CURRENCY: UNPROCESSABLE_ENTITY,
    EXCHANGE_RATE_UNAVAILABLE: HTTP_SERVICE_UNAVAILABLE,
    POLICY_NOT_EDITABLE: HTTP_CONFLICT,
    POLICY_CANNOT_ACTIVATE: HTTP_CONFLICT,
    // Reservation
    BOOKING_CONFLICT: HTTP_CONFLICT,
    INVALID_DATES: UNPROCESSABLE_ENTITY,
    INVALID_OCCUPANCY: UNPROCESSABLE_ENTITY,
    GUEST_REQUIRED: UNPROCESSABLE_ENTITY,
    PROPERTY_INACTIVE: HTTP_CONFLICT,
    RATE_PLAN_NOT_APPLICABLE: UNPROCESSABLE_ENTITY,
    RESERVATION_NOT_FOUND: HTTP_NOT_FOUND,
    RESERVATION_ALREADY_CANCELLED: HTTP_CONFLICT,
    RESERVATION_CANNOT_MODIFY: HTTP_CONFLICT,
    RESERVATION_CANNOT_CANCEL: HTTP_CONFLICT,
    CANCELLATION_PENALTY_APPLIED: HTTP_CONFLICT,
    BOOKING_REFERENCE_COLLISION: HTTP_CONFLICT,
    RESERVATION_CONFLICT: HTTP_CONFLICT,
    // PriceQuote validation/conversion (reservation flow)
    QUOTE_NOT_FOUND: HTTP_NOT_FOUND,
    QUOTE_ALREADY_CONVERTED: HTTP_CONFLICT,
    QUOTE_EXPIRED: HTTP_CONFLICT,
    QUOTE_PROPERTY_MISMATCH: UNPROCESSABLE_ENTITY,
    QUOTE_ROOM_MISMATCH: UNPROCESSABLE_ENTITY,
    QUOTE_RATE_MISMATCH: UNPROCESSABLE_ENTITY,
    QUOTE_DATE_MISMATCH: UNPROCESSABLE_ENTITY,
    QUOTE_OCCUPANCY_MISMATCH: UNPROCESSABLE_ENTITY,
    QUOTE_CURRENCY_MISMATCH: UNPROCESSABLE_ENTITY,
    // 403, not 422 like the shape mismatches: the quote is well-formed, the
    // caller simply does not hold the eligibility context it was priced under.
    QUOTE_CONTEXT_MISMATCH: HTTP_FORBIDDEN,
    // PMS
    PMS_TIMEOUT: HTTP_GATEWAY_TIMEOUT,
    PMS_AUTH_FAILED: BAD_GATEWAY,
    PMS_UNAVAILABLE: HTTP_SERVICE_UNAVAILABLE,
    PMS_SYNC_ERROR: BAD_GATEWAY,
    // PMS webhook ingestion (Task 33)
    // Deliberately 401 with no detail: telling a caller why verification
    // failed helps only an attacker.
    PMS_WEBHOOK_SIGNATURE_INVALID: HTTP_UNAUTHORIZED,
    PMS_WEBHOOK_PAYLOAD_INVALID: BAD_REQUEST,
    PMS_MAPPING_NOT_FOUND: HTTP_NOT_FOUND,
    PMS_CHAIN_NOT_RESOLVED: HTTP_NOT_FOUND,
    PMS_TAX_TREATMENT_UNCONFIGURED: INTERNAL_SERVER_ERROR,
    // Integration
    INTEGRATION_TIMEOUT: HTTP_GATEWAY_TIMEOUT,
    INTEGRATION_FAILED: BAD_GATEWAY,
    INTEGRATION_UNAVAILABLE: HTTP_SERVICE_UNAVAILABLE,
    RETRY_EXHAUSTED: BAD_GATEWAY,
    DEAD_LETTER_REACHED: BAD_GATEWAY,
    SYNC_CONFLICT: HTTP_CONFLICT,
    SYNC_FAILED: BAD_GATEWAY,
    EVENT_PROCESSING_FAILED: INTERNAL_SERVER_ERROR,
    // Payment
    PAYMENT_FAILED: PAYMENT_REQUIRED,
    PAYMENT_DECLINED: PAYMENT_REQUIRED,
    PAYMENT_TIMEOUT: HTTP_GATEWAY_TIMEOUT,
    INVALID_PAYMENT_TOKEN: UNPROCESSABLE_ENTITY,
    FRAUD_DETECTED: HTTP_FORBIDDEN,
    PAYMENT_GATEWAY_UNAVAILABLE: HTTP_SERVICE_UNAVAILABLE,
    PAYMENT_AUTH_FAILED: BAD_GATEWAY,
    PAYMENT_NOT_FOUND: HTTP_NOT_FOUND,
    CAPTURE_EXCEEDS_AUTHORIZATION: UNPROCESSABLE_ENTITY,
    REFUND_EXCEEDS_CAPTURE: UNPROCESSABLE_ENTITY,
    PAYMENT_ALREADY_CAPTURED: HTTP_CONFLICT,
    PAYMENT_ALREADY_VOIDED: HTTP_CONFLICT,
    PAYMENT_CURRENCY_MISMATCH: UNPROCESSABLE_ENTITY,
    PAYMENT_STATE_INVALID: HTTP_CONFLICT,
    PAYMENT_POLICY_INCOMPLETE: UNPROCESSABLE_ENTITY,
    RECONCILIATION_DISCREPANCY: HTTP_CONFLICT,
    PAYMENT_FIELD_NOT_ALLOWED: BAD_REQUEST,
    // Notification
    NOTIFICATION_FAILED: INTERNAL_SERVER_ERROR,
    TEMPLATE_NOT_FOUND: HTTP_NOT_FOUND,
    PROVIDER_ERROR: BAD_GATEWAY,
    INVALID_RECIPIENT: UNPROCESSABLE_ENTITY,
    TEMPLATE_RENDER_ERROR: INTERNAL_SERVER_ERROR,
    QUEUE_FULL: HTTP_SERVICE_UNAVAILABLE,
    DELIVERY_FAILED: BAD_GATEWAY,
    GATEWAY_NOT_CONFIGURED: HTTP_SERVICE_UNAVAILABLE,
    // Attachments
    ATTACHMENT_NOT_FOUND: HTTP_NOT_FOUND,
    ATTACHMENT_STORAGE_NOT_CONFIGURED: BAD_REQUEST,
    ATTACHMENT_STORAGE_MISCONFIGURED: BAD_REQUEST,
    ATTACHMENT_TYPE_NOT_ALLOWED: BAD_REQUEST,
    ATTACHMENT_TOO_LARGE: HTTP_PAYLOAD_TOO_LARGE,
    ATTACHMENT_KEY_OUT_OF_SCOPE: BAD_REQUEST,
    ATTACHMENT_OCR_FAILED: BAD_GATEWAY,
    // Tenancy
    TENANT_NOT_FOUND: HTTP_NOT_FOUND,
    TENANT_MISMATCH: HTTP_FORBIDDEN,
    TENANT_SUSPENDED: HTTP_FORBIDDEN,
    // Deliberately 403, not 401 like TOKEN_EXPIRED/SESSION_EXPIRED: the
    // operator's own session is still valid, only the impersonation grant lapsed.
    IMPERSONATION_EXPIRED: HTTP_FORBIDDEN,
    TENANT_CONTEXT_MISSING: INTERNAL_SERVER_ERROR,
    // Reference data and bulk import
    REFERENCE_IN_USE: HTTP_CONFLICT,
    REFERENCE_RESTORE_CONFLICT: HTTP_CONFLICT,
    IMPORT_FILE_TOO_LARGE: HTTP_PAYLOAD_TOO_LARGE,
    IMPORT_FILE_UNREADABLE: UNPROCESSABLE_ENTITY,
    IMPORT_ROWS_INVALID: UNPROCESSABLE_ENTITY,
    IMPORT_PLAN_NOT_FOUND: HTTP_NOT_FOUND,
    IMPORT_PLAN_STALE: HTTP_CONFLICT,
};
/** Type guard: is this string a registered error code? */
export function isErrorCode(value) {
    return Object.hasOwn(ERROR_HTTP_STATUS, value);
}
/**
 * HTTP status for a code.
 *
 * `ERROR_HTTP_STATUS` is typed as total over `ErrorCode`, so this cannot miss.
 * The registry-completeness test enforces that the type and the object agree.
 */
export function httpStatusForCode(code) {
    return ERROR_HTTP_STATUS[code];
}
//# sourceMappingURL=codes.js.map